import { IPromise } from 'angular';
import { createActions } from '@manager-base/app-base/config/data-access';
import { ArchitecturalRequestDTO } from '@manager-base/data/dtos/associations/architectural-request.dto';
import { BASE_PATH_API } from '@manager-base/app-base/environment/paths/constants';
import { CreateArchitecturalRequestDTO } from '@manager-base/data/dtos/associations/create-architectural-request.dto';
import { PagingResultsDTO } from '@manager-base/data/dtos/paging-results.dto';
import { ArchitecturalRequestSummaryDTO } from '@manager-base/data/dtos/associations/architectural-request-summary.dto';
import { AssociationArchitecturalRequestSettingsDTO } from '@manager-base/data/dtos/associations/association-architectural-request-settings.dto';
import { UpsertAssociationArchitecturalRequestCommitteeDTO } from '@manager-base/data/dtos/associations/upsert-association-architectural-request-committee.dto';
import { UpsertAssociationArchitecturalRequestSettingsDTO } from '@manager-base/data/dtos/associations/upsert-association-architectural-request-settings.dto';
import { UpdateArchitecturalRequestDTO } from '@manager-base/data/dtos/associations/update-architectural-request.dto';
import { UpdateArchitecturalRequestNameDTO } from '@manager-base/data/dtos/associations/update-architectural-request-name.dto';
import { UpdateArchitecturalRequestDecisionDTO } from '@manager-base/data/dtos/associations/update-architectural-request-decision.dto';
import { UpdateArchitecturalRequestDueDateDTO } from '@manager-base/data/dtos/associations/update-architectural-request-due-date.dto';
import { ArchitecturalRequestDetailsDTO } from '@manager-base/data/dtos/associations/architectural-request-details.dto';
import { UpdateArchitecturalRequestStatusDTO } from '@manager-base/data/dtos/associations/update-architectural-request-status.dto';
import { ArchitecturalRequestHistoryDTO } from '@manager-base/data/dtos/associations/architectural-request-history.dto';
import { SendArchitecturalRequestDecisionEmailDTO } from '@manager-base/data/dtos/associations/send-architectural-request-decision-email.dto';
import { SendArchitecturalRequestDecisionMailingDTO } from '@manager-base/data/dtos/associations/send-architectural-request-decision-mailing.dto';

const actions = {
    createAssociationArchitecturalRequest: {
        method: 'POST',
        url: `${BASE_PATH_API}/v1/associations/:associationId/architecturalRequests`,
    },

    getAssociationArchitecturalRequests: {
        method: 'GET',
        url: `${BASE_PATH_API}/v1/associations/:associationId/architecturalRequests`,
    },

    getAssociationArchitecturalRequest: {
        method: 'GET',
        url: `${BASE_PATH_API}/v1/associations/:associationId/architecturalRequests/:architecturalRequestId`,
    },

    deleteAssociationArchitecturalRequest: {
        method: 'DELETE',
        url: `${BASE_PATH_API}/v1/associations/:associationId/architecturalRequests/:architecturalRequestId`,
    },

    getAssociationArchitecturalRequestSettings: {
        method: 'GET',
        url: `${BASE_PATH_API}/v1/associations/:associationId/architecturalRequests/settings`,
    },

    getAssociationArchitecturalRequestSettingsCached: {
        method: 'GET',
        url: `${BASE_PATH_API}/v1/associations/:associationId/architecturalRequests/settings`,
        cache: true,
    },

    upsertAssociationArchitecturalRequestSettings: {
        method: 'PUT',
        url: `${BASE_PATH_API}/v1/associations/:associationId/architecturalRequests/settings`,
    },

    upsertAssociationArchitecturalRequestCommitteeSettings: {
        method: 'PUT',
        url: `${BASE_PATH_API}/v1/associations/:associationId/architecturalRequests/settings/committee`,
    },

    getAssociationArchitecturalRequestIsResidentCenterEnabledSetting: {
        method: 'GET',
        url: `${BASE_PATH_API}/v1/associations/:associationId/architecturalRequests/settings/isResidentCenterEnabled`,
    },

    upsertAssociationArchitecturalRequestIsResidentCenterEnabledSetting: {
        method: 'PUT',
        url: `${BASE_PATH_API}/v1/associations/:associationId/architecturalRequests/settings/isResidentCenterEnabled`,
    },

    updateArchitecturalRequest: {
        method: 'PUT',
        url: `${BASE_PATH_API}/v1/associations/:associationId/architecturalRequests/:architecturalRequestId`,
    },

    updateArchitecturalRequestName: {
        method: 'PUT',
        url: `${BASE_PATH_API}/v1/associations/:associationId/architecturalRequests/:architecturalRequestId/name`,
    },

    updateArchitecturalRequestDecision: {
        method: 'PUT',
        url: `${BASE_PATH_API}/v1/associations/:associationId/architecturalRequests/:architecturalRequestId/decision`,
    },

    updateArchitecturalRequestDueDate: {
        method: 'PUT',
        url: `${BASE_PATH_API}/v1/associations/:associationId/architecturalRequests/:architecturalRequestId/dueDate`,
    },

    updateArchitecturalRequestStatus: {
        method: 'PUT',
        url: `${BASE_PATH_API}/v1/associations/:associationId/architecturalRequests/:architecturalRequestId/status`,
    },

    deleteArchitecturalRequestFile: {
        method: 'DELETE',
        url: `${BASE_PATH_API}/v1/associations/:associationId/architecturalRequests/:architecturalRequestId/file/:fileId`,

    },

    getAllArchitecturalRequestHistories: {
        method: 'GET',
        url: `${BASE_PATH_API}/v1/associations/:associationId/architecturalRequests/:architecturalRequestId/history`,
    },

    sendArchitecturalRequestDecisionEmail: {
        method: 'POST',
        url: `${BASE_PATH_API}/v1/associations/:associationId/architecturalRequests/:architecturalRequestId/decisionEmail`,
    },

    sendArchitecturalRequestDecisionMailing: {
        method: 'POST',
        url: `${BASE_PATH_API}/v1/associations/:associationId/architecturalRequests/:architecturalRequestId/decisionMailing`,
    },
};

export interface IAssociationArchitecturalRequestsApi {
    createAssociationArchitecturalRequest(params: { associationId: number }, dto: CreateArchitecturalRequestDTO): IPromise<ArchitecturalRequestDTO>;
    getAssociationArchitecturalRequests(params: { associationId: number, skip?: number, take?: number, orderBy?: string, orderByAscending?: boolean }): IPromise<PagingResultsDTO<ArchitecturalRequestSummaryDTO>>;
    getAssociationArchitecturalRequest(params: { associationId: number, architecturalRequestId: number }): IPromise<ArchitecturalRequestDetailsDTO>;
    deleteAssociationArchitecturalRequest(params: { associationId: number, architecturalRequestId: number }): IPromise<void>;
    getAssociationArchitecturalRequestSettings(params: { associationId: number }): IPromise<AssociationArchitecturalRequestSettingsDTO>;
    getAssociationArchitecturalRequestSettingsCached(params: { associationId: number }): IPromise<AssociationArchitecturalRequestSettingsDTO>;
    upsertAssociationArchitecturalRequestSettings(params: { associationId: number }, dto: UpsertAssociationArchitecturalRequestSettingsDTO): IPromise<AssociationArchitecturalRequestSettingsDTO>;
    upsertAssociationArchitecturalRequestCommitteeSettings(params: { associationId: number }, dto: UpsertAssociationArchitecturalRequestCommitteeDTO): IPromise<AssociationArchitecturalRequestSettingsDTO>;
    getAssociationArchitecturalRequestIsResidentCenterEnabledSetting(params: { associationId: number }): IPromise<boolean>;
    upsertAssociationArchitecturalRequestIsResidentCenterEnabledSetting(params: { associationId: number }, enabled: string): IPromise<AssociationArchitecturalRequestSettingsDTO>;
    updateArchitecturalRequest(params: { associationId: number, architecturalRequestId: number }, dto: UpdateArchitecturalRequestDTO): IPromise<ArchitecturalRequestDTO>;
    updateArchitecturalRequestName(params: { associationId: number, architecturalRequestId: number }, dto: UpdateArchitecturalRequestNameDTO): IPromise<ArchitecturalRequestDTO>;
    updateArchitecturalRequestDecision(params: { associationId: number, architecturalRequestId: number }, dto: UpdateArchitecturalRequestDecisionDTO): IPromise<ArchitecturalRequestDTO>;
    updateArchitecturalRequestDueDate(params: { associationId: number, architecturalRequestId: number }, dto: UpdateArchitecturalRequestDueDateDTO): IPromise<ArchitecturalRequestDTO>;
    updateArchitecturalRequestStatus(params: { associationId: number, architecturalRequestId: number }, dto: UpdateArchitecturalRequestStatusDTO): IPromise<ArchitecturalRequestDTO>;
    deleteArchitecturalRequestFile(params: { associationId: number, architecturalRequestId: number, fileId: number }): IPromise<void>;
    getAllArchitecturalRequestHistories(params: { associationId: number, architecturalRequestId: number, skip?: number, take?: number }): IPromise<PagingResultsDTO<ArchitecturalRequestHistoryDTO>>;
    sendArchitecturalRequestDecisionEmail(params: { associationId: number, architecturalRequestId: number }, dto: SendArchitecturalRequestDecisionEmailDTO): IPromise<void>;
    sendArchitecturalRequestDecisionMailing(params: { associationId: number, architecturalRequestId: number }, dto: SendArchitecturalRequestDecisionMailingDTO): IPromise<void>;
}

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function AssociationArchitecturalRequestsApi(): IAssociationArchitecturalRequestsApi {
    return createActions(actions);
}
